import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '../../lib/supabase.js';
import {
  AUTH_SESSION_STALE_MESSAGE,
  clearStaleLocalAuth,
  isAuthNetworkOrRefreshError,
  revalidateAuthSession,
} from '../../lib/authSessionRecovery.js';
import { AUTH_INIT_MS, runAppDataFetch, DEFAULT_APP_FETCH_MS } from '../../lib/appDataFetch.js';
import { publishAppSession } from '../../lib/appSessionBridge.js';
import { logSiteZone } from '../../lib/userAnalytics.js';
import { filterInventoryByMarketplaceRules } from '../itemFilters.js';
import { BANNERS_HERO_GRADIENTS } from '../marketplaceConstants.js';
import {
  heroSlideFromPublicBanner,
  resolvePublicBanner,
} from '../../lib/publicBannerAssets.js';
import { useMarketplaceFilterSlice } from './useMarketplaceFilterSlice.js';
import { fetchListings, fetchPublicListingById } from '../../lib/listingsService.js';
import { invokeAwardGrooves } from '../../lib/aiScansService.js';
import { claimReferralCode } from '../../lib/referralsService.js';
import { fetchActiveAdsBySlot, mapAdToHeroSlide, trackAdImpression } from '../../lib/adsService.js';
import {
  addWishlistListing,
  removeWishlistListing,
  mergeGuestWishlistIntoAccount,
} from '../../lib/wishlistsService.js';

const LIKES_STORAGE_KEY = 'dreamvinyl_marketplace_likes_v1';

/** Año y número de semana ISO (UTC) para limitar invocaciones a award-grooves (racha_semanal). */
function utcIsoYearWeek() {
  const now = new Date();
  const t = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  const dayNum = t.getUTCDay() || 7;
  t.setUTCDate(t.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(((t - yearStart) / 86400000 + 1) / 7);
  return `${t.getUTCFullYear()}-W${String(weekNo).padStart(2, '0')}`;
}

/** Mapa ruta interna `page` → zona analítica (panel admin). */
const ZONE_BY_PAGE = {
  marketplace: 'marketplace',
  subastas: 'subastas',
  comunidad: 'comunidad',
  'comunidad-guardados': 'comunidad',
  'grooves-ranking': 'comunidad',
  noticias: 'noticias',
  chat: 'chat',
  perfil: 'perfil',
  'perfil-vendedor': 'perfil',
  ajustes: 'ajustes',
  publicar: 'publicar',
  admin: 'admin',
  'mis-ventas': 'ventas',
  pedido: 'pedido',
  ficha: 'ficha_disco',
  notificaciones: 'notificaciones',
  'partner-registro': 'partner',
  'registro-tienda': 'partner',
  'partner-panel': 'partner',
  'tienda-entradas': 'entradas',
  valoraciones: 'valoraciones',
  'mis-solicitudes': 'perfil',
  'grooves-checkout': 'perfil',
  'marketplace-checkout': 'pedido',
  legal: 'legal',
  'centro-ayuda': 'ayuda',
};

/**
 * Estado y lógica del home del marketplace (listado, filtros, auth de sesión, splash, banners).
 * Dos buscadores independientes (misma UI): `filters` mercado, `subastas` solo afecta listado de subastas.
 */
export function useMarketplaceHome() {
  const [user, setUser] = useState(null);
  /** true tras el primer getSession (evita deep link ?listing= como anónimo antes de hidratar admin). */
  const [authReady, setAuthReady] = useState(false);
  const [sessionNotice, setSessionNotice] = useState(null);
  /** Incrementa en reconexión / refresh global → todas las pantallas recargan datos. */
  const [appDataRefreshKey, setAppDataRefreshKey] = useState(0);
  const dismissSessionNotice = useCallback(() => setSessionNotice(null), []);
  const reconnectBusyRef = useRef(false);
  const [listings, setListings] = useState([]);
  const [loadingListings, setLoadingListings] = useState(true);
  const [errorListings, setErrorListings] = useState(null);

  const [page, setPage] = useState('marketplace');
  const [selectedItem, setSelectedItem] = useState(null);
  const [chatSeller, setChatSeller] = useState(null);

  const [showSplash, setShowSplash] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalTab, setModalTab] = useState('login');
  const [bannerHero, setBannerHero] = useState(0);
  const [heroAds, setHeroAds] = useState([]);
  const [vista, setVista] = useState(4);
  const [vistaSub, setVistaSub] = useState(4);
  const [currentPageSub, setCurrentPageSub] = useState(1);
  const [likes, setLikes] = useState(() => {
    try {
      const raw = typeof localStorage !== 'undefined' && localStorage.getItem(LIKES_STORAGE_KEY);
      if (raw) return JSON.parse(raw);
    } catch (_) {}
    return {};
  });
  const [favoritesOnlyMode, setFavoritesOnlyMode] = useState(false);
  /** Al abrir Ajustes desde «Editar» en perfil: `'cuenta' | 'privacidad' | ...` */
  const [ajustesInitialSub, setAjustesInitialSub] = useState(null);
  /** Tras `navigate('subastas', { openNuevaSubasta: true })` (p. ej. Mis ventas): abrir modal «Nueva subasta» una vez. */
  const [subastasOpenNuevaSubasta, setSubastasOpenNuevaSubasta] = useState(false);
  /** UUID de noticia desde `?noticia=` (se consume al abrir el modal en PageNoticias). */
  const [pendingDeepLinkNoticiaId, setPendingDeepLinkNoticiaId] = useState(null);
  /** UUID de publicación comunidad desde `?comunidad=` (scroll en PageComunidad). */
  const [pendingDeepLinkComunidadId, setPendingDeepLinkComunidadId] = useState(null);
  /** UUID de listing desde `?listing=` (abre ficha en PageFichaDisc). */
  const [pendingDeepLinkListingId, setPendingDeepLinkListingId] = useState(null);
  /** Tras volver de Stripe: 'success' | 'cancel' */
  const [groovesCheckoutOutcome, setGroovesCheckoutOutcome] = useState(null);
  const [groovesCheckoutSessionId, setGroovesCheckoutSessionId] = useState(null);
  const [marketplaceCheckoutOutcome, setMarketplaceCheckoutOutcome] = useState(null);
  const [marketplaceCheckoutSessionId, setMarketplaceCheckoutSessionId] = useState(null);
  const [pedidoHighlightTransactionId, setPedidoHighlightTransactionId] = useState(null);
  const clearPedidoHighlightTransactionId = useCallback(() => {
    setPedidoHighlightTransactionId(null);
  }, []);
  /** Cuenta suspendida / en revisión — ver `AccountModerationWall` en Marketplace. */
  const [accountGate, setAccountGate] = useState(null);

  /** Vista «⋮⋮» (8) eliminada: migrar a cuadrícula ⊞ si quedara en estado antiguo. */
  useEffect(() => {
    setVista(v => (v === 8 ? 4 : v));
    setVistaSub(v => (v === 8 ? 4 : v));
  }, []);

  const mpFilters = useMarketplaceFilterSlice();
  const subFilters = useMarketplaceFilterSlice();
  const [currentPage, setCurrentPage] = useState(1);

  const splashAutoCloseTimerRef = useRef(null);
  const ensuredProfilesRef = useRef(new Set());
  const lastHeroAdImpressedRef = useRef(null);
  const [publicHeroSlide, setPublicHeroSlide] = useState(null);

  const defaultHeroSlides = useMemo(() => {
    const slides = [];
    if (publicHeroSlide) slides.push(publicHeroSlide);
    slides.push(...BANNERS_HERO_GRADIENTS);
    return slides;
  }, [publicHeroSlide]);

  const heroSlides = useMemo(() => {
    const fromDb = (heroAds || [])
      .filter(ad => String(ad.media_url || '').trim())
      .map(mapAdToHeroSlide)
      .filter(Boolean);
    if (!fromDb.length) return defaultHeroSlides;
    return [...fromDb, ...defaultHeroSlides];
  }, [heroAds, defaultHeroSlides]);

  useEffect(() => {
    let cancelled = false;
    resolvePublicBanner('banner_hero').then(asset => {
      if (cancelled) return;
      setPublicHeroSlide(asset ? heroSlideFromPublicBanner(asset) : null);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchActiveAdsBySlot('hero_marketplace', 12).then(list => {
      if (cancelled) return;
      setHeroAds(list || []);
    });
    const onVis = () => {
      if (document.visibilityState !== 'visible') return;
      fetchActiveAdsBySlot('hero_marketplace', 12).then(list => {
        if (!cancelled) setHeroAds(list || []);
      });
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const uuidRe =
        /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      const params = new URLSearchParams(window.location.search);
      const rawNoticia = params.get('noticia')?.trim();
      const rawComunidad = params.get('comunidad')?.trim();
      const rawListing = params.get('listing')?.trim();
      const rawGroovesCheckout = params.get('grooves_checkout')?.trim().toLowerCase();
      const rawMarketplaceCheckout = params.get('marketplace_checkout')?.trim().toLowerCase();
      const rawSessionId = params.get('session_id')?.trim();
      const u = new URL(window.location.href);
      let changed = false;
      if (rawNoticia && uuidRe.test(rawNoticia)) {
        setPage('noticias');
        setPendingDeepLinkNoticiaId(rawNoticia);
        u.searchParams.delete('noticia');
        changed = true;
      }
      if (rawComunidad && uuidRe.test(rawComunidad)) {
        setPage('comunidad');
        setPendingDeepLinkComunidadId(rawComunidad);
        u.searchParams.delete('comunidad');
        changed = true;
      }
      if (rawListing && uuidRe.test(rawListing)) {
        setPendingDeepLinkListingId(rawListing);
        u.searchParams.delete('listing');
        changed = true;
      }
      if (rawGroovesCheckout === 'success' || rawGroovesCheckout === 'cancel') {
        setPage('grooves-checkout');
        setGroovesCheckoutOutcome(rawGroovesCheckout);
        setGroovesCheckoutSessionId(
          rawSessionId && /^cs_[a-zA-Z0-9_]+$/.test(rawSessionId) ? rawSessionId : null
        );
        u.searchParams.delete('grooves_checkout');
        u.searchParams.delete('session_id');
        changed = true;
      }
      if (rawMarketplaceCheckout === 'success' || rawMarketplaceCheckout === 'cancel') {
        setPage('marketplace-checkout');
        setMarketplaceCheckoutOutcome(rawMarketplaceCheckout);
        setMarketplaceCheckoutSessionId(
          rawSessionId && /^cs_[a-zA-Z0-9_]+$/.test(rawSessionId) ? rawSessionId : null
        );
        u.searchParams.delete('marketplace_checkout');
        u.searchParams.delete('session_id');
        changed = true;
      }
      if (!changed) return;
      const qs = u.searchParams.toString();
      window.history.replaceState({}, '', u.pathname + (qs ? `?${qs}` : '') + (u.hash || ''));
    } catch (_) {
      /* noop */
    }
  }, []);

  useEffect(() => {
    const listingId = pendingDeepLinkListingId;
    if (!listingId || !authReady) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await fetchPublicListingById(listingId);
      if (cancelled) return;
      setPendingDeepLinkListingId(null);
      if (data && !error) {
        setSelectedItem(data);
        setPage('ficha');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pendingDeepLinkListingId, authReady]);

  useEffect(() => {
    if (bannerHero < heroSlides.length) return;
    setBannerHero(0);
  }, [bannerHero, heroSlides.length]);

  const itemsPerPage = vista === 4 ? 12 : 8;

  const mpf = mpFilters.filters;
  const sf = subFilters.filters;

  const computedMaxPrice = useMemo(() => {
    let max = 0;
    for (const it of listings || []) {
      const p = it?.isAuction ? Number(it.currentBid ?? it.price ?? 0) : Number(it.price ?? 0);
      if (Number.isFinite(p) && p > max) max = p;
    }
    // UI: redondear a múltiplos de 5, mínimo 50 para no romper el slider en catálogos pequeños
    const rounded = Math.ceil(max / 5) * 5;
    return Math.max(50, rounded || 50);
  }, [listings]);

  // Mantener el rango de precio "sin límite" por defecto = [0, max catálogo]
  useEffect(() => {
    mpf.setPrecioMaxUi?.(computedMaxPrice);
    sf.setPrecioMaxUi?.(computedMaxPrice);

    // Si el usuario no ha ajustado el precio (sigue en 0..300 o supera el nuevo máximo), ajustar al nuevo máximo.
    mpf.setPrecioRange?.(r => {
      const cur = Array.isArray(r) ? r : [0, computedMaxPrice];
      const maxCur = Number(cur[1]);
      if (!Number.isFinite(maxCur) || maxCur === 300 || maxCur > computedMaxPrice) return [Number(cur[0]) || 0, computedMaxPrice];
      return cur;
    });
    sf.setPrecioRange?.(r => {
      const cur = Array.isArray(r) ? r : [0, computedMaxPrice];
      const maxCur = Number(cur[1]);
      if (!Number.isFinite(maxCur) || maxCur === 300 || maxCur > computedMaxPrice) return [Number(cur[0]) || 0, computedMaxPrice];
      return cur;
    });
  }, [computedMaxPrice, mpf, sf]);

  const mpGeoFilter = useMemo(() => {
    const km = mpf.distanciaKmFiltro;
    if (km == null) return null;
    const uLat = Number(mpf.userLat);
    const uLng = Number(mpf.userLng);
    if (!Number.isFinite(uLat) || !Number.isFinite(uLng) || !(Number(km) > 0)) return null;
    return { userLat: uLat, userLng: uLng, distanciaKm: Number(km) };
  }, [mpf.distanciaKmFiltro, mpf.userLat, mpf.userLng]);

  const subGeoFilter = useMemo(() => {
    const km = sf.distanciaKmFiltro;
    if (km == null) return null;
    const uLat = Number(sf.userLat);
    const uLng = Number(sf.userLng);
    if (!Number.isFinite(uLat) || !Number.isFinite(uLng) || !(Number(km) > 0)) return null;
    return { userLat: uLat, userLng: uLng, distanciaKm: Number(km) };
  }, [sf.distanciaKmFiltro, sf.userLat, sf.userLng]);

  /* eslint-disable react-hooks/exhaustive-deps -- `mpf`/`sf`: deps = campos primitivos; el objeto `filters` cambia de referencia cada render */
  const allItemsFiltered = useMemo(
    () => filterInventoryByMarketplaceRules(listings, mpf, { favoritesOnlyMode, likes, geoFilter: mpGeoFilter }),
    [
      listings,
      mpf.filtrosAplicados,
      mpf.tiposSel,
      mpf.condFiltros,
      mpf.generosSel,
      mpf.genFiltros,
      mpf.decadaFiltros,
      mpf.precioRange,
      mpf.busqueda,
      mpf.ubicacionSelector,
      mpf.ordenar,
      favoritesOnlyMode,
      likes,
      mpGeoFilter,
    ]
  );

  const auctionItemsFiltered = useMemo(
    () =>
      filterInventoryByMarketplaceRules(listings.filter(i => i.isAuction), sf, {
        favoritesOnlyMode: false,
        likes: {},
        geoFilter: subGeoFilter,
      }),
    [
      listings,
      sf.filtrosAplicados,
      sf.tiposSel,
      sf.condFiltros,
      sf.generosSel,
      sf.genFiltros,
      sf.decadaFiltros,
      sf.precioRange,
      sf.busqueda,
      sf.ubicacionSelector,
      sf.ordenar,
      subGeoFilter,
    ]
  );
  /* eslint-enable react-hooks/exhaustive-deps */

  const itemsPerPageSub = vistaSub === 4 ? 12 : 8;
  const totalPagesSub = Math.max(1, Math.ceil(auctionItemsFiltered.length / itemsPerPageSub));
  const auctionPageItems = useMemo(
    () => auctionItemsFiltered.slice((currentPageSub - 1) * itemsPerPageSub, currentPageSub * itemsPerPageSub),
    [auctionItemsFiltered, currentPageSub, itemsPerPageSub]
  );
  const gridColsSub =
    vistaSub === 1 ? '1fr' : 'repeat(auto-fill,minmax(182px,1fr))';

  useEffect(() => {
    setCurrentPageSub(p => (p > totalPagesSub ? totalPagesSub : p));
  }, [totalPagesSub]);

  const totalPages = Math.max(1, Math.ceil(allItemsFiltered.length / itemsPerPage));

  const pageItems = useMemo(
    () => allItemsFiltered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage),
    [allItemsFiltered, currentPage, itemsPerPage]
  );

  const gridCols =
    vista === 1 ? '1fr' : 'repeat(auto-fill,minmax(182px,1fr))';

  /** Cierre inmediato: no animación de salida (evita capa fija invisible con z-index que bloquea clics). */
  const closeSplash = useCallback(() => {
    if (splashAutoCloseTimerRef.current) {
      clearTimeout(splashAutoCloseTimerRef.current);
      splashAutoCloseTimerRef.current = null;
    }
    setShowSplash(false);
  }, []);

  const navigate = useCallback((p, arg2 = null, seller = null) => {
    if (p === 'eventos') p = 'comunidad';
    if (p === 'legal') {
      setSubastasOpenNuevaSubasta(false);
      const tipoRaw = arg2 && typeof arg2 === 'object' && arg2.tipo != null ? String(arg2.tipo) : 'aviso';
      const legalTipo = ['aviso', 'privacidad', 'terminos', 'cookies'].includes(tipoRaw) ? tipoRaw : 'aviso';
      setSelectedItem({ legalTipo });
      setPage('legal');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (p === 'perfil-vendedor' && arg2 && typeof arg2 === 'object' && (arg2.username || arg2.seller)) {
      setSubastasOpenNuevaSubasta(false);
      setPage('perfil-vendedor');
      setSelectedItem({
        seller: String(arg2.username || arg2.seller).replace(/^@/, ''),
        sellerUserId: arg2.userId ?? null,
        sellerAvatarUrl: arg2.avatarUrl ?? null,
        returnTo: arg2.returnTo ?? null,
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (p === 'admin') {
      setSubastasOpenNuevaSubasta(false);
      const secRaw =
        arg2 && typeof arg2 === 'object' && arg2.adminActive != null ? String(arg2.adminActive).trim() : '';
      const comunidadTabRaw =
        arg2 && typeof arg2 === 'object' && arg2.adminComunidadTab != null
          ? String(arg2.adminComunidadTab).trim()
          : '';
      const adminNav = {};
      if (secRaw) adminNav.adminActive = secRaw;
      if (comunidadTabRaw) adminNav.adminComunidadTab = comunidadTabRaw;
      setSelectedItem(Object.keys(adminNav).length ? adminNav : null);
      setPage('admin');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    const favNav = arg2 && typeof arg2 === 'object' && arg2.favoritesOnly === true;

    if (p === 'ajustes') {
      if (arg2 && typeof arg2 === 'object' && arg2.initialSub) {
        setAjustesInitialSub(String(arg2.initialSub));
      } else {
        setAjustesInitialSub(null);
      }
    }

    if (p === 'subastas') {
      setSubastasOpenNuevaSubasta(
        !!(arg2 && typeof arg2 === 'object' && arg2.openNuevaSubasta === true)
      );
    } else {
      setSubastasOpenNuevaSubasta(false);
    }

    if (p === 'pedido' && arg2 && typeof arg2 === 'object' && arg2.highlightTransactionId) {
      setPedidoHighlightTransactionId(String(arg2.highlightTransactionId));
    } else if (p !== 'pedido') {
      setPedidoHighlightTransactionId(null);
    }

    if (p === 'comunidad' && arg2 && typeof arg2 === 'object' && arg2.comunidadId) {
      setPendingDeepLinkComunidadId(String(arg2.comunidadId));
    }

    const item =
      favNav ? null : p === 'ajustes' ? null : p === 'subastas' ? null : arg2;
    setPage(p);
    if (p === 'marketplace') setFavoritesOnlyMode(!!favNav);
    if (p === 'publicar') {
      setSelectedItem(arg2 ?? null);
    } else if (item) {
      setSelectedItem(item);
    }
    if (seller) setChatSeller(seller);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const goBack = useCallback(() => {
    setPage('marketplace');
    setSelectedItem(null);
    setChatSeller(null);
    setSubastasOpenNuevaSubasta(false);
  }, []);

  const clearSubastasOpenNuevaSubasta = useCallback(() => {
    setSubastasOpenNuevaSubasta(false);
  }, []);

  const clearPendingDeepLinkNoticiaId = useCallback(() => setPendingDeepLinkNoticiaId(null), []);
  const clearPendingDeepLinkComunidadId = useCallback(() => setPendingDeepLinkComunidadId(null), []);
  const clearGroovesCheckoutReturn = useCallback(() => {
    setGroovesCheckoutOutcome(null);
    setGroovesCheckoutSessionId(null);
  }, []);

  const clearMarketplaceCheckoutReturn = useCallback(() => {
    setMarketplaceCheckoutOutcome(null);
    setMarketplaceCheckoutSessionId(null);
  }, []);

  const toggleLike = useCallback(
    id => {
      if (!id) return;
      setLikes(p => {
        const willLike = !p[id];
        const next = { ...p };
        if (willLike) next[id] = true;
        else delete next[id];
        const uid = user?.id;
        if (uid) {
          if (willLike) addWishlistListing(uid, id);
          else removeWishlistListing(uid, id);
        }
        return next;
      });
    },
    [user?.id]
  );

  useEffect(() => {
    if (user?.id) return;
    try {
      localStorage.setItem(LIKES_STORAGE_KEY, JSON.stringify(likes));
    } catch (_) {}
  }, [likes, user?.id]);

  useEffect(() => {
    const uid = user?.id;
    if (!uid) return;
    let cancelled = false;
    (async () => {
      let guestIds = [];
      try {
        const raw = typeof localStorage !== 'undefined' && localStorage.getItem(LIKES_STORAGE_KEY);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed && typeof parsed === 'object') {
            guestIds = Object.keys(parsed).filter(k => parsed[k] === true);
          }
        }
      } catch (_) {}
      const { likes: merged, error } = await mergeGuestWishlistIntoAccount(uid, guestIds);
      if (cancelled) return;
      if (!error) {
        setLikes(merged);
        try {
          localStorage.removeItem(LIKES_STORAGE_KEY);
        } catch (_) {}
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  /** Mercado: recargar anuncios tras publicar o reconexión global. */
  useEffect(() => {
    if (page !== 'marketplace' || !authReady) return undefined;
    let cancelled = false;
    setLoadingListings(true);
    setErrorListings(null);
    runAppDataFetch(() => fetchListings(), {
      timeoutMs: DEFAULT_APP_FETCH_MS,
      timeoutMessage: 'Tiempo de espera al cargar el mercado. Revisa la conexión o actualiza.',
    })
      .then(({ data, error }) => {
        if (cancelled) return;
        setListings(data);
        setErrorListings(error);

        const rows = Array.isArray(data) ? data : [];
        const liveIds = new Set(rows.map(r => r.id).filter(Boolean));
        setLikes(prev => {
          if (!prev || typeof prev !== 'object') return prev;
          const next = {};
          let changed = false;
          for (const [k, v] of Object.entries(prev)) {
            if (v !== true) continue;
            if (liveIds.has(k)) next[k] = true;
            else changed = true;
          }
          return changed ? next : prev;
        });
      })
      .catch(err => {
        if (cancelled) return;
        setListings([]);
        setErrorListings(err?.message || 'No se pudieron cargar los anuncios.');
      })
      .finally(() => {
        if (!cancelled) setLoadingListings(false);
      });
    return () => {
      cancelled = true;
    };
  }, [page, authReady, appDataRefreshKey]);

  const limpiar = useCallback(() => {
    mpFilters.actions.limpiar();
    setFavoritesOnlyMode(false);
    setCurrentPage(1);
  }, [mpFilters.actions]);

  const limpiarSubastas = useCallback(() => {
    subFilters.actions.limpiar();
  }, [subFilters.actions]);

  const actions = useMemo(
    () => ({
      ...mpFilters.actions,
      limpiar,
      toggleLike,
      closeSplash,
    }),
    [mpFilters.actions, limpiar, toggleLike, closeSplash]
  );

  const refreshAccountGate = useCallback(async () => {
    const uid = user?.id;
    if (!uid) {
      setAccountGate(null);
      return;
    }
    const { data, error } = await supabase
      .from('profiles')
      .select('account_status, account_moderation_note, is_admin')
      .eq('id', uid)
      .maybeSingle();
    if (error) {
      setAccountGate(null);
      return;
    }
    if (data?.is_admin) {
      setAccountGate(null);
      return;
    }
    const st = data?.account_status || 'active';
    if (st === 'blocked') {
      setAccountGate({ mode: 'blocked', note: data.account_moderation_note || null });
    } else if (st === 'paused_review') {
      setAccountGate({ mode: 'paused', note: data.account_moderation_note || null });
    } else {
      setAccountGate(null);
    }
  }, [user?.id]);

  useEffect(() => {
    refreshAccountGate();
  }, [refreshAccountGate]);

  useEffect(() => {
    const uid = user?.id;
    if (!uid || !page) return;
    const z = ZONE_BY_PAGE[page] || 'otro';
    logSiteZone(uid, z);
  }, [user?.id, page]);

  useEffect(() => {
    runAppDataFetch(() => supabase.auth.getSession(), {
      timeoutMs: AUTH_INIT_MS,
      timeoutMessage: 'Tiempo de espera al iniciar sesión.',
    })
      .then(({ data, error }) => {
        if (error && isAuthNetworkOrRefreshError(error) && typeof navigator !== 'undefined' && !navigator.onLine) {
          return;
        }
        if (error && !isAuthNetworkOrRefreshError(error)) {
          clearStaleLocalAuth().then(() => setSessionNotice(AUTH_SESSION_STALE_MESSAGE));
        }
        setUser(data?.session?.user ?? null);
      })
      .catch(err => {
        if (isAuthNetworkOrRefreshError(err) && typeof navigator !== 'undefined' && !navigator.onLine) return;
        if (!isAuthNetworkOrRefreshError(err)) {
          clearStaleLocalAuth().then(() => setSessionNotice(AUTH_SESSION_STALE_MESSAGE));
        }
        setUser(null);
      })
      .finally(() => {
        setAuthReady(true);
      });

    const ensureProfile = async u => {
      const id = u?.id;
      if (!id || ensuredProfilesRef.current.has(id)) return;
      ensuredProfilesRef.current.add(id);
      const email = u.email || '';
      const fallbackUsername = email ? email.split('@')[0] : `user_${id.slice(0, 8)}`;
      const username =
        u.user_metadata?.username ||
        u.user_metadata?.preferred_username ||
        u.user_metadata?.full_name ||
        u.user_metadata?.name ||
        fallbackUsername;

      // Solo INSERT si falta fila: nunca resetear grooves/grooves_total en login (upsert con grooves:0 borraba saldos de prueba).
      await supabase.from('profiles').upsert(
        {
          id,
          username: String(username).trim(),
          email,
          grooves: 0,
          grooves_total: 0,
          level: 1,
        },
        { onConflict: 'id', ignoreDuplicates: true }
      );

      /* Bonus registro_bienvenida: trigger BD al verificar teléfono (award_grooves exige phone_verified_at). */
    };

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, s) => {
      const u = s?.user ?? null;
      setUser(u);
      if (event === 'SIGNED_IN' && u) await ensureProfile(u);
      if (event === 'PASSWORD_RECOVERY') {
        setShowModal(true);
        setModalTab('reset');
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  /** Tras corte de red o pestaña en segundo plano: revalidar sesión y recargar feed. */
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const runReconnect = async () => {
      if (reconnectBusyRef.current) return;
      reconnectBusyRef.current = true;
      try {
        const hadUserId = user?.id ?? null;
        const { user: nextUser, expired, networkError } = await revalidateAuthSession({ hadUserId });
        setUser(nextUser);
        if (expired) setSessionNotice(AUTH_SESSION_STALE_MESSAGE);
        setAppDataRefreshKey(k => k + 1);
        if (!networkError && nextUser?.id) refreshAccountGate();
      } finally {
        reconnectBusyRef.current = false;
      }
    };

    const onOnline = () => {
      void runReconnect();
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') void runReconnect();
    };

    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [user?.id, refreshAccountGate]);

  /** Puente global: cualquier pantalla puede leer sesión y señal de recarga. */
  useEffect(() => {
    publishAppSession({ authReady, dataRefreshKey: appDataRefreshKey, user });
  }, [authReady, appDataRefreshKey, user]);

  /** ?ref=codigo en URL: intenta registrar referido una vez por sesión de usuario. */
  useEffect(() => {
    const uid = user?.id;
    if (!uid || typeof window === 'undefined') return;
    const key = `dv_ref_claimed_${uid}`;
    try {
      if (sessionStorage.getItem(key)) return;
    } catch {
      /* ignore */
    }
    let ref = '';
    try {
      ref = new URLSearchParams(window.location.search).get('ref') || '';
    } catch {
      return;
    }
    ref = String(ref).trim();
    if (ref.length < 4) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await claimReferralCode(ref);
        if (cancelled) return;
        if (r?.ok) {
          try {
            sessionStorage.setItem(key, '1');
          } catch {
            /* ignore */
          }
        }
      } catch (e) {
        console.warn('[useMarketplaceHome] claim_referral:', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  /** Racha semanal: Edge + RPC (idempotencia por semana en BD); throttle local por semana ISO. */
  useEffect(() => {
    const uid = user?.id;
    if (!uid) return;
    const wk = utcIsoYearWeek();
    const storageKey = `dv_grooves_racha_ok_${uid}_${wk}`;
    try {
      if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(storageKey)) return;
    } catch {
      /* ignore */
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await invokeAwardGrooves({ reason: 'racha_semanal', refId: uid, refType: 'profile' });
        if (cancelled || !r?.ok) return;
        try {
          if (typeof sessionStorage !== 'undefined') sessionStorage.setItem(storageKey, '1');
        } catch {
          /* ignore */
        }
      } catch (e) {
        console.warn('[useMarketplaceHome] racha_semanal:', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  useEffect(() => {
    if (!showSplash) return;
    if (splashAutoCloseTimerRef.current) {
      clearTimeout(splashAutoCloseTimerRef.current);
      splashAutoCloseTimerRef.current = null;
    }
    splashAutoCloseTimerRef.current = setTimeout(() => {
      splashAutoCloseTimerRef.current = null;
      closeSplash();
    }, 10000);
    return () => {
      if (splashAutoCloseTimerRef.current) {
        clearTimeout(splashAutoCloseTimerRef.current);
        splashAutoCloseTimerRef.current = null;
      }
    };
  }, [showSplash, closeSplash]);

  useEffect(() => {
    const current = heroSlides[bannerHero];
    const slideType = String(current?.type || '').toLowerCase();
    const isLong =
      current &&
      (slideType === 'video' ||
        (slideType === 'ad' && String(current.media_type || '').toLowerCase() === 'video'));
    const delayMs = isLong ? 9000 : 5000;
    const len = Math.max(1, heroSlides.length);
    const t = setTimeout(() => setBannerHero(b => (b + 1) % len), delayMs);
    return () => clearTimeout(t);
  }, [bannerHero, heroSlides]);

  useEffect(() => {
    const current = heroSlides[bannerHero];
    if (!current || String(current.type || '').toLowerCase() !== 'ad') return;
    if (!current.id) return;
    if (lastHeroAdImpressedRef.current === current.id) return;
    lastHeroAdImpressedRef.current = current.id;
    trackAdImpression(current.id);
  }, [bannerHero, heroSlides]);

  useEffect(() => {
    setCurrentPage(1);
  }, [mpf.busqueda, mpf.ubicacionSelector, favoritesOnlyMode, mpf.distanciaKmFiltro, mpf.userLat, mpf.userLng, mpf.ordenar]);

  useEffect(() => {
    setCurrentPageSub(1);
  }, [
    sf.filtrosAplicados,
    sf.busqueda,
    sf.ubicacionSelector,
    sf.tiposSel,
    sf.condFiltros,
    sf.generosSel,
    sf.genFiltros,
    sf.decadaFiltros,
    sf.precioRange,
    sf.distanciaKmFiltro,
    sf.userLat,
    sf.userLng,
  ]);

  const filters = useMemo(
    () => ({
      ...mpFilters.filters,
      currentPage,
      setCurrentPage,
    }),
    [mpFilters.filters, currentPage]
  );

  return {
    auth: {
      user,
      authReady,
      appDataRefreshKey,
      accountGate,
      refreshAccountGate,
      sessionNotice,
      dismissSessionNotice,
    },
    navigation: {
      page,
      setPage,
      selectedItem,
      setSelectedItem,
      chatSeller,
      setChatSeller,
      navigate,
      goBack,
      favoritesOnlyMode,
      ajustesInitialSub,
      clearAjustesInitialSub: () => setAjustesInitialSub(null),
      subastasOpenNuevaSubasta,
      clearSubastasOpenNuevaSubasta,
      pendingDeepLinkNoticiaId,
      clearPendingDeepLinkNoticiaId,
      pendingDeepLinkComunidadId,
      clearPendingDeepLinkComunidadId,
      groovesCheckoutOutcome,
      groovesCheckoutSessionId,
      clearGroovesCheckoutReturn,
      marketplaceCheckoutOutcome,
      marketplaceCheckoutSessionId,
      clearMarketplaceCheckoutReturn,
      pedidoHighlightTransactionId,
      clearPedidoHighlightTransactionId,
    },
    ui: {
      showSplash,
      setShowSplash,
      showModal,
      setShowModal,
      modalTab,
      setModalTab,
      bannerHero,
      setBannerHero,
      heroSlides,
      vista,
      setVista,
      tooltipCond: mpFilters.tooltipCond,
      setTooltipCond: mpFilters.setTooltipCond,
      likes,
    },
    filters,
    subastas: {
      filters: subFilters.filters,
      actions: subFilters.actions,
      tooltipCond: subFilters.tooltipCond,
      setTooltipCond: subFilters.setTooltipCond,
      limpiarSubastas,
      vista: vistaSub,
      setVista: setVistaSub,
      currentPage: currentPageSub,
      setCurrentPage: setCurrentPageSub,
      pageItems: auctionPageItems,
      totalPages: totalPagesSub,
      gridCols: gridColsSub,
    },
    list: {
      allItemsFiltered,
      auctionItemsFiltered,
      totalPages,
      items: pageItems,
      itemsPerPage,
      gridCols,
      loading: loadingListings,
      error: errorListings,
    },
    actions,
  };
}
